import { computed, ref } from 'vue';

// TalkRobin: install the dashboard as an app (public/manifest.json).
// Chrome, Edge and most Android browsers offer an install prompt once, early
// in the page's life, so it's kept here until it's used. iPhones and iPads,
// and Android browsers without the prompt (e.g. Firefox), add the app from
// the browser's menu instead, so they get the steps.
const PROMPT_GRACE_MS = 4000;

const deferredPrompt = ref(null);
const installed = ref(false);
// Browsers fire the prompt shortly after load; until then, don't fall back
// to manual steps.
const promptWaitOver = ref(false);

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault();
    deferredPrompt.value = event;
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt.value = null;
    installed.value = true;
  });
  setTimeout(() => {
    promptWaitOver.value = true;
  }, PROMPT_GRACE_MS);
  // Installable from the first page (login included), and the offline page
  // works before signing in. Push notifications reuse this registration.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  }
}

export const isRunningAsApp = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

// iPadOS reports itself as a Mac, so touch support tells them apart.
export const isAppleMobile = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  (window.navigator.platform === 'MacIntel' &&
    window.navigator.maxTouchPoints > 1);

export const isAndroid = () => /android/i.test(window.navigator.userAgent);

export function useInstallApp() {
  const canPrompt = computed(() => Boolean(deferredPrompt.value));
  const needsAppleSteps = computed(
    () => !canPrompt.value && isAppleMobile() && !isRunningAsApp()
  );
  const needsAndroidSteps = computed(
    () =>
      !canPrompt.value &&
      promptWaitOver.value &&
      isAndroid() &&
      !isRunningAsApp()
  );
  // Which steps to show when there's no install prompt: 'apple', 'android'.
  const manualSteps = computed(() => {
    if (needsAppleSteps.value) return 'apple';
    if (needsAndroidSteps.value) return 'android';
    return null;
  });
  const canInstall = computed(
    () =>
      !installed.value &&
      !isRunningAsApp() &&
      (canPrompt.value || Boolean(manualSteps.value))
  );

  const promptInstall = async () => {
    const event = deferredPrompt.value;
    if (!event) return false;
    deferredPrompt.value = null;
    event.prompt();
    const { outcome } = await event.userChoice;
    return outcome === 'accepted';
  };

  return {
    canInstall,
    canPrompt,
    needsAppleSteps,
    manualSteps,
    promptInstall,
  };
}
